<template>
  <ModalTunnel
    :visible="visible"
    :loading="loading"
    :closeOnExternalClick="false"
    :maxHeightContent="
      currentPageKey === pageKeys.MAP
        ? 'calc(100vh - 73px)'
        : 'calc(100vh - 315px)'
    "
    :width="currentPageKey === pageKeys.MAP ? 'calc(100vw - 46px)' : undefined"
    :nbPages="nbTunnelPages"
    :displayNavigation="false"
    ref="refTunnel"
  >
    <!-- Map -->
    <template #[`content-${pages.MAP.pageNum}`]>
      <MPCMap
        :createMode="creating && visible"
        :showMap="visible"
        v-model:location="selectedLocation"
        v-model:criticality="criticality"
        v-model:provider="providerIdSelected"
        v-model:requireGPU="requireGPU"
        @update:location="resetNodePoolList"
      />
    </template>

    <template #[`footer-${pages.MAP.pageNum}`]>
      <MPFDefault
        :previousLabel="pages.MAP.previousLabel"
        :nextLabel="pages.MAP.nextLabel"
        :disableNextButton="!selectedLocation"
        @previous="askCloseConfirmation"
        @next="goToPage(pageKeys.CLUSTER_CONFIG)"
      />
    </template>
    <!-- / Map -->

    <!-- Cluster config -->
    <template #[`header-${pages.CLUSTER_CONFIG.pageNum}`]>
      <MPHDefault
        v-model:header="name"
        :headerEditable="true"
        :closeButton="true"
        :headerEditCall="headerEditCall"
        @close="askCloseConfirmation"
      />
    </template>

    <template #[`content-${pages.CLUSTER_CONFIG.pageNum}`]>
      <MPCClusterConfig
        :provider="providerIdSelected"
        :providerLabel="providerLabel"
        :location="selectedLocation"
        :nodePoolList="nodePoolList"
        :nbMasterNodes="{
          min: masterNodes.min?.length ?? 0,
          max: masterNodes.max?.length ?? 0,
        }"
        :errorMsg="
          nodePoolList.length && notEnoughtNodes
            ? `This cluster needs a minimum of ${minWorkerNodeRequirements} worker node${
                minWorkerNodeRequirements > 1 ? 's' : ''
              }`
            : ''
        "
        :hasFransysAccount="canCurrentProviderUseFransysAccount"
        :customerAccounts="customerProviderAccounts"
        v-model:selectedInfrastructureProviderAccountId="
          selectedInfrastructureProviderAccountId
        "
        v-model:isAutoPilot="isAutoPilot"
        v-model:safety="safety"
        v-model:maxBudget="maxBudget"
        @connectAccount="goToConnectedServices"
        @goToAddNodePool="goToPage(pageKeys.ADD_NODE_POOL)"
        @deleteNodePool="deleteNodePool"
      />
    </template>

    <template #[`footer-${pages.CLUSTER_CONFIG.pageNum}`]>
      <MPFClusterConfig
        :isAutoPilot="isAutoPilot"
        :provider="providerIdSelected"
        :location="selectedLocation"
        :nodePoolList="nodePoolList"
        :masterNodes="masterNodes"
        :nbNodes="nbNodes"
        :publishState="buttonStatePublish"
        :publishDisabled="
          disableBtnPublish || notEnoughtNodes || !hasAnyAccountOption || !selectedLocation?.id
        "
        :publishLoading="saving"
        @publish="accept(true)"
        @previous="goToPage(pageKeys.MAP)"
      />
    </template>
    <!-- / Cluster config -->

    <!-- Add node pool -->
    <template #[`header-${pages.ADD_NODE_POOL.pageNum}`]>
      <MPHDefault
        :header="pages.ADD_NODE_POOL.header"
        :prevButton="true"
        :closeButton="false"
        @prev="goToPage(pageKeys.CLUSTER_CONFIG)"
      />
    </template>

    <template #[`content-${pages.ADD_NODE_POOL.pageNum}`]>
      <MPCAddNodePool
        :provider="providerIdSelected"
        :location="selectedLocation"
        @typologySelected="onDraftTypologySelected"
      />
    </template>

    <template #[`footer-${pages.ADD_NODE_POOL.pageNum}`]>
      <MPFDefault
        :disableNextButton="!draftTypology"
        :previousLabel="pages.ADD_NODE_POOL.previousLabel"
        :nextLabel="pages.ADD_NODE_POOL.nextLabel"
        @previous="goToPage(pageKeys.CLUSTER_CONFIG)"
        @next="createNodePool"
      />
    </template>
    <!-- / Add node pool -->
  </ModalTunnel>
</template>

<script lang="ts">
import { Vue, Options, prop } from "vue-class-component";
import ModalTunnel from "@/components/UIElements/ModalTunnel.vue";
import CustomButton from "@/components/UIElements/CustomButton.vue";
import Slider from "@/components/UIElements/Slider.vue";
import SegmentedControl from "@/components/UIElements/SegmentedControl.vue";
import API from "@/api/wrapper";
import PriceDisplay from "@/components/UIElements/PriceDisplay.vue";
import Icon from "@/components/UIElements/Icon.vue";
import {
  APICluster,
  APIClusterNodeTypologie,
  APIClusterRequirement,
  APIClusterLocation,
  APIInfrastructureProviderAccount,
  APIInfrastructureProvider,
} from "@/typesAPI";
import { Watch } from "vue-property-decorator";
import CustomTextInput from "@/components/UIElements/CustomTextInput.vue";
import Tooltip from "@/components/UIElements/Tooltip.vue";
import Divider from "@/components/UIElements/Divider.vue";
import Badge from "@/components/UIElements/Badge.vue";

import { ref } from "vue";
import { NodePool } from "@/types";
import MPHDefault from "@/components/Modals/Partials/Headers/MPHDefault.vue";
import MPCAddNodePool from "@/components/Modals/Partials/Contents/MPCAddNodePool.vue";
import MPCCriticality from "@/components/Modals/Partials/Contents/MPCCriticality.vue";
import MPCMap from "@/components/Modals/Partials/Contents/MPCMap.vue";
import MPCClusterConfig from "@/components/Modals/Partials/Contents/MPCClusterConfig.vue";
import MPFDefault from "@/components/Modals/Partials/Footers/MPFDefault.vue";
import MPFClusterConfig from "@/components/Modals/Partials/Footers/MPFClusterConfig.vue";

class Props {
  cluster: APICluster | null = prop({
    required: true,
  });
  creating?: boolean = prop({
    required: false,
  });
}

interface Page {
  pageNum: number;
  header?: string;
  nextLabel?: string;
  previousLabel?: string;
}

@Options({
  components: {
    ModalTunnel,
    CustomButton,
    Slider,
    SegmentedControl,
    Icon,
    CustomTextInput,
    PriceDisplay,
    Tooltip,
    Divider,
    Badge,
    MPFDefault,
    MPHDefault,
    MPCAddNodePool,
    MPCCriticality,
    MPCMap,
    MPCClusterConfig,
    MPFClusterConfig,
  },
})
export default class ModalClusterConfig extends Vue.with(Props) {
  selectedLocation: APIClusterLocation | null = null;
  refTunnel: any = ref(null);
  nbTunnelPages = 3;
  pageKeys = {
    MAP: "MAP",
    CLUSTER_CONFIG: "CLUSTER_CONFIG",
    ADD_NODE_POOL: "ADD_NODE_POOL",
  };
  pages: { [key: string]: Page } = {
    [this.pageKeys.MAP]: {
      pageNum: 0,
      nextLabel: "Continue",
      previousLabel: "Cancel",
    },
    [this.pageKeys.CLUSTER_CONFIG]: {
      pageNum: 1,
    },
    [this.pageKeys.ADD_NODE_POOL]: {
      pageNum: 2,
      header: "Add node pool",
      nextLabel: "Add node pool",
      previousLabel: "Previous",
    },
  };
  currentPageKey = "MAP";

  nodePoolList: NodePool[] = [];
  draftTypology: APIClusterNodeTypologie | null = null;
  isAutoPilot = true; // This will be used to determine if the cluster is in auto-scaling mode or not.
  safety = 15; // This will be used to determine the safety margin for auto-scaling.
  maxBudget = 500; // This will be used to determine the maximum amount for auto-scaling.
  criticality = ""; // This will be used to determine the criticality of the cluster.

  name = "New cluster";
  loading = false;
  saving = false;
  disableBtnPublish = false;
  buttonStatePublish = "";
  requireGPU = false;
  test = "";
  selectedInfrastructureProviderAccountId: string | null = null;

  goToPage(pageKey: string) {
    this.currentPageKey = pageKey;
    this.refTunnel?.goToPage(this.pages[pageKey].pageNum);
  }

  setParamsToDefault() {
    this.isAutoPilot = true;
    this.safety = 15;
    this.maxBudget = 500;
    this.criticality = this.$enums.ClusterCriticality.STABLE;
    this.nodePoolList = [];
    this.providerIdSelected = "";
    this.selectedLocation = null;
  }

  onDraftTypologySelected(typology: APIClusterNodeTypologie) {
    // This method should be called when a typology is selected in the MPCAddNodePool component.
    // It will set the selected typology.
    this.draftTypology = typology;
  }

  resetNodePoolList() {
    this.nodePoolList = [];
  }

  deleteNodePool(index: number) {
    // This method should remove a node pool from the list.
    if (index >= 0 && index < this.nodePoolList.length) {
      this.nodePoolList.splice(index, 1);
    }
  }

  createNodePool() {
    // This method should trigger the creation of a new node pool
    // and navigate to the cluster config page.
    if (this.draftTypology) {
      this.nodePoolList.push({
        min_size: 4,
        max_size: 18,
        target_size: 4,
        typology: this.draftTypology.id,
      });
      this.draftTypology = null; // Reset the draft typology after adding
    }

    this.goToPage(this.pageKeys.CLUSTER_CONFIG);
  }

  get providers(): APIInfrastructureProvider[] {
    return this.$store.getters["clusters/getProviders"];
  }

  get customerInfrastructureAccounts(): APIInfrastructureProviderAccount[] {
    if (!this.visible) return [];
    return this.$store.getters["providerAccounts/getAccountList"];
  }

  get nbNodes(): { min: number; max: number } {
    if (!this.nodePoolList?.length) {
      return { min: 0, max: 0 };
    }
    if (!this.isAutoPilot) {
      const n = this.nodePoolList.reduce(
        (accumulator, val) => accumulator + val.target_size,
        0,
      );
      return { min: n, max: n };
    }
    return {
      min: this.nodePoolList.reduce(
        (accumulator, val) => accumulator + val.min_size,
        0,
      ),
      max: this.nodePoolList.reduce(
        (accumulator, val) => accumulator + val.max_size,
        0,
      ),
    };
  }

  get masterNodes(): {
    min: APIClusterNodeTypologie[];
    max: APIClusterNodeTypologie[];
  } {
    if (!this.isAutoPilot) {
      return { min: [], max: [] };
    }

    return {
      min: this.$helpers.clusters.getTypologiesFromRequirements(
        this.providerRequirements,
        this.clusterNodeTypologies,
        this.nbNodes.min,
        this.$enums.RequirementTypes.MASTER,
        this.criticality,
      ) as APIClusterNodeTypologie[],
      max: this.$helpers.clusters.getTypologiesFromRequirements(
        this.providerRequirements,
        this.clusterNodeTypologies,
        this.nbNodes.max,
        this.$enums.RequirementTypes.MASTER,
        this.criticality,
      ) as APIClusterNodeTypologie[],
    };
  }

  get providerRequirements(): APIClusterRequirement[] {
    if (!this.selectedLocation || !this.providerIdSelected) {
      return [];
    }
    return this.$store.getters[
      "clusters/getClusterRequirementsByProviderAndLocation"
    ](this.providerIdSelected, this.selectedLocation?.id);
  }

  get clusterNodeTypologies(): APIClusterNodeTypologie[] {
    if (!this.providerIdSelected) {
      return [];
    }
    return this.$store.getters[
      "clusters/getClusterNodeTypologiesByProviderAndLocation"
    ](this.providerIdSelected, this.selectedLocation?.id);
  }

  get minWorkerNodeRequirements(): number {
    return this.$helpers.clusters.getMinAmountFromRequirements(
      this.providerRequirements,
      this.nbNodes.min,
      this.$enums.RequirementTypes.WORKER,
      this.criticality,
    );
  }

  get notEnoughtNodes(): boolean {
    if (this.isAutoPilot && !this.nodePoolList.length) return false;
    return this.minWorkerNodeRequirements > this.nbNodes.min;
  }

  askCloseConfirmation() {
    if (!this.hasConfigChanged) {
      this.onClose();
      return;
    }
    this.$store.dispatch("modals/askConfirmation", {
      title: "Close without saving?",
      text: "Are you sure you want to close and lose all changes?",
      confirmButtonText: "Close without saving",
      cancelButtonText: "Cancel",
      onConfirm: () => {
        this.onClose();
      },
    });
  }

  onClose() {
    this.$store.dispatch("modals/setVisibility", {
      modal: this.$enums.ModalsName.Cluster,
      newState: false,
    });
    this.$router.push({ name: this.$route.name as string });

    // Reset modal state after fading out
    setTimeout(() => {
      this.onclusterChange();
    }, 300);
  }

  headerEditCall(header: string) {
    return API.clusters
      .editCluster(this.cluster?.id ?? "", { name: header, deploy_now: false })
      .then((newCluster: APICluster) => {
        this.$store.dispatch("clusters/editCluster", newCluster);
      });
  }

  accept(publishNow: boolean) {
    const infrastructureProviderPayload = this.infrastructureProviderPayload;
    if (!infrastructureProviderPayload) {
      return;
    }

    const requested_config: APICluster["attributes"]["requested_config"] = {
      criticality: this.criticality,
      auto_pilot: {
        enabled: this.isAutoPilot,
      },
      node_pools: [],
    };
    if (this.isAutoPilot) {
      // requested_config.auto_pilot.safety_margin = this.safety
      if (!this.nodePoolList.length) {
        requested_config.auto_pilot.max_budget = this.maxBudget * 100; // Convert to cents
      }
    }

    this.nodePoolList.forEach((nodePool: NodePool) => {
      const trimedPool: Partial<NodePool> = {
        typology: nodePool.typology,
      };
      if (this.isAutoPilot) {
        trimedPool.max_size = nodePool.max_size;
        trimedPool.min_size = nodePool.min_size;
      } else {
        trimedPool.target_size = nodePool.target_size;
      }

      requested_config.node_pools.push(trimedPool);
    });

    if (this.cluster && this.selectedLocation?.id) {
      this.saving = true;

      const body = this.$helpers.ressources.getOnlyDifferentFrom(
        {
          name: this.name,
          deploy_now: publishNow ? true : false,
          region: this.selectedLocation.id,
          ...infrastructureProviderPayload,
          requested_config,
        },
        this.cluster.attributes,
      );
      if (
        body.requested_config?.criticality ===
        this.cluster.attributes.requested_config.criticality
      ) {
        delete body.requested_config.criticality;
      }

      API.clusters
        .editCluster(this.cluster.id, body)
        .then((newCluster: APICluster) => {
          this.$store.dispatch("clusters/editCluster", newCluster);

          if (publishNow) {
            this.buttonStatePublish = this.$enums.ButtonStates.Success;
          } else {
            this.buttonStatePublish = this.$enums.ButtonStates.Neutral;
          }

          this.disableBtnPublish = true;

          this.closeTimeout = setTimeout(() => {
            this.onClose();
          }, this.successCloseDelay);
        })
        .finally(() => {
          this.saving = false;
        });
    } else if (this.creating && this.selectedLocation?.id) {
      this.saving = true;
      API.clusters
        .create({
          name: this.name,
          deploy_now: publishNow ? true : false,
          region: this.selectedLocation?.id,
          ...infrastructureProviderPayload,
          requested_config,
        })
        .then((newCluster: APICluster) => {
          this.$store.dispatch("clusters/editCluster", newCluster);

          if (publishNow) {
            this.buttonStatePublish = this.$enums.ButtonStates.Success;
          } else {
            this.buttonStatePublish = this.$enums.ButtonStates.Neutral;
          }

          this.disableBtnPublish = true;
          this.closeTimeout = setTimeout(() => {
            this.onClose();
          }, this.successCloseDelay);
        })
        .finally(() => {
          this.saving = false;
        });
    }
  }

  upgradePlanLoading = false;
  closeTimeout: number | undefined = undefined;
  successCloseDelay = 1000;
  inited = false;
  defaultProvider = "hetzner";
  providerIdSelected = this.defaultProvider;
  timerRefreshLocalisations = 5 * 60 * 1000; //5min
  intervalRefreshLocations = 0;

  setSelectedLocationToClusterLocation(force?: boolean) {
    if (this.locations.length) {
      if ((force || !this.selectedLocation) && this.cluster) {
        this.selectedLocation =
          this.locations.find(
            (loc) => loc.id === this.cluster?.attributes.region,
          ) ?? null;
      }
    }
  }

  @Watch("locations")
  onLocationsChange() {
    if (this.cluster) {
      this.setSelectedLocationToClusterLocation();
    }
  }

  init(): void {
    this.setParamsToDefault();
    this.inited = true;
  }

  upgradePlan() {
    this.$helpers.subscription.checkout();
    this.upgradePlanLoading = true;
  }

  get providerLabel(): string {
    return this.$helpers.providers.providerToLabel(this.providerIdSelected);
  }

  get hasAnyAccountOption(): boolean {
    return (
      this.canCurrentProviderUseFransysAccount ||
      this.customerProviderAccounts.length > 0
    );
  }

  get canCurrentProviderUseFransysAccount(): boolean {
    return (
      this.providers.find(
        (provider: APIInfrastructureProvider) =>
          provider.id.toLowerCase() === this.providerIdSelected.toLowerCase(),
      )?.attributes.has_fransys_account ?? false
    );
  }

  get customerProviderAccounts(): { id: string; name: string }[] {
    return this.customerInfrastructureAccounts
      .filter(
        (account: APIInfrastructureProviderAccount) =>
          account.attributes.provider.toLowerCase() ===
          this.providerIdSelected.toLowerCase(),
      )
      .map((account: APIInfrastructureProviderAccount) => ({
        id: account.id,
        name: account.attributes.name,
        provider: account.attributes.provider,
      }));
  }

  get infrastructureProviderPayload(): {
    provider?: string;
    infrastructure_provider_account_id?: string;
  } | null {
    if (this.selectedInfrastructureProviderAccountId === null) {
      return {
        provider: this.providerIdSelected,
      };
    }
    return {
      infrastructure_provider_account_id:
        this.selectedInfrastructureProviderAccountId,
    };
  }

  goToConnectedServices() {
    this.onClose();
    this.$router.push({ name: "connectedServices" });
  }

  setDefaultInfrastructureAccountSelection() {
    this.selectedInfrastructureProviderAccountId = null;
    if (
      this.cluster &&
      this.cluster.attributes.provider === this.providerIdSelected
    ) {
      const clusterInfrastructureProviderAccountId =
        this.cluster.attributes.infrastructure_provider_account_id;
      if (
        clusterInfrastructureProviderAccountId &&
        this.customerProviderAccounts.find(
          (account) => account.id === clusterInfrastructureProviderAccountId,
        )
      ) {
        this.selectedInfrastructureProviderAccountId =
          clusterInfrastructureProviderAccountId;
        return;
      }
      if (this.canCurrentProviderUseFransysAccount) {
        this.selectedInfrastructureProviderAccountId = null;
        return;
      } else if (this.customerProviderAccounts.length) {
        this.selectedInfrastructureProviderAccountId =
          this.customerProviderAccounts[0].id;
        return;
      }
    }
  }

  get visible(): boolean {
    return this.$store.getters["modals/getVisibilityCluster"];
  }

  get locations(): APIClusterLocation[] {
    return this.providerIdSelected
      ? this.$store.getters["clusters/getLocationsByProvider"](
          this.providerIdSelected,
        )
      : [];
  }

  get startingConfig(): { [key: string]: any } {
    if (this.cluster) {
      return {
        isAutoPilot:
          this.cluster?.attributes.requested_config.auto_pilot.enabled ?? false,
        safety:
          this.cluster?.attributes.requested_config.auto_pilot.safety_margin ??
          this.safety,
        maxBudget: this.cluster?.attributes.requested_config?.auto_pilot
          ?.max_budget
          ? (this.cluster.attributes.requested_config.auto_pilot
              .max_budget as number) / 100
          : this.maxBudget,
        criticality:
          this.cluster?.attributes.requested_config.criticality ??
          this.criticality,
        nodePoolList:
          (JSON.parse(
            JSON.stringify(
              this.cluster?.attributes.requested_config?.node_pools,
            ),
          ) as NodePool[]) ?? [],
        providerIdSelected:
          this.cluster?.attributes?.provider ?? this.defaultProvider,
        selectedInfrastructureProviderAccountId:
          this.cluster?.attributes?.infrastructure_provider_account_id || null,
        selectedLocation:
          this.locations?.find(
            (loc) => loc.id === this.cluster?.attributes.region,
          ) ?? null,
      };
    }
    return {
      isAutoPilot: true,
      safety: 15,
      maxBudget: 500,
      criticality: this.$enums.ClusterCriticality.STABLE,
      nodePoolList: [],
      providerIdSelected: "",
      selectedInfrastructureProviderAccountId: this
        .canCurrentProviderUseFransysAccount
        ? null
        : this.customerProviderAccounts[0]?.id ?? undefined,
      selectedLocation: null,
    };
  }

  get hasConfigChanged(): boolean {
    const config = this.startingConfig;
    for (const key of Object.keys(config)) {
      // @ts-ignore
      if (JSON.stringify(this[key]) !== JSON.stringify(config[key])) {
        return true;
      }
    }
    return false;
  }

  @Watch("cluster", { immediate: true })
  onclusterChange() {
    if (!this.visible) return;
    if (!this.inited) {
      this.init();
    }
    this.buttonStatePublish = this.$enums.ButtonStates.Neutral;
    this.disableBtnPublish = false;

    if (this.cluster) {
      this.isAutoPilot =
        this.cluster?.attributes.requested_config.auto_pilot.enabled ?? false;
      this.safety =
        this.cluster?.attributes.requested_config.auto_pilot.safety_margin ??
        this.safety;
      this.maxBudget = this.cluster?.attributes.requested_config?.auto_pilot
        ?.max_budget
        ? (this.cluster.attributes.requested_config.auto_pilot
            .max_budget as number) / 100
        : this.maxBudget;
      this.criticality =
        this.cluster?.attributes.requested_config.criticality ??
        this.criticality;
      this.nodePoolList =
        (JSON.parse(
          JSON.stringify(this.cluster?.attributes.requested_config?.node_pools),
        ) as NodePool[]) ?? [];
      this.name = this.cluster?.attributes?.name;
      this.providerIdSelected =
        this.cluster?.attributes?.provider ?? this.defaultProvider;
      this.selectedInfrastructureProviderAccountId =
        this.cluster?.attributes?.infrastructure_provider_account_id || null;
      this.setSelectedLocationToClusterLocation(true);
      this.goToPage(this.pageKeys.CLUSTER_CONFIG);
    } else {
      this.name = "New cluster";
      this.setParamsToDefault();
      this.selectedInfrastructureProviderAccountId = null;
      this.goToPage(this.pageKeys.MAP);
    }
    this.setDefaultInfrastructureAccountSelection();
  }

  @Watch("providerIdSelected")
  onProviderChange() {
    if (!this.visible) return;
    this.setDefaultInfrastructureAccountSelection();
  }

  @Watch("customerInfrastructureAccounts")
  onCustomerInfrastructureAccountsChange() {
    if (!this.visible) return;
    this.setDefaultInfrastructureAccountSelection();
  }

  @Watch("visible", { immediate: true })
  onVisibleChange(newVal: boolean) {
    if (newVal) {
      this.onclusterChange();
    }
  }
}
</script>

<style lang="scss" scoped>
@import "@/css/variables";

.custom-thumb {
  width: 128px;
  max-width: 128px;
  border: 1px solid $color-neutral-grey-12;
  border-radius: 8px;
}
.card-typo {
  border: 1px solid $color-neutral-grey-12;
  border-radius: 8px;
  transition: border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
  padding: 12px;
  margin-bottom: 16px;
  cursor: pointer;
  &:hover {
    border: 1px solid $color-neutral-grey-24;
    box-shadow: none;
  }

  &.disabled {
    border: 1px solid $color-neutral-grey-12;
    background: $color-neutral-grey-12;
    cursor: default !important;
  }
  &.selected {
    padding-top: 11px;
    padding-left: 11px;
    margin-bottom: 15px;
    border: 2px solid $color-brand;
    box-shadow: 0px 0px 0px 2px rgba(50, 25, 178, 0.24);
  }
}
.alert-trial {
  border: 1px solid $color-orange;
  color: $text-error;
  background: $bg-error;
  border-radius: 8px;
}
.master-nodes-info {
  background-color: $color-neutral-grey-8 !important;
  width: auto !important;
}

.tag {
  cursor: pointer;
}
</style>
